import { useCallback, useEffect, useState } from 'react'
import { api, ApiError, formatDateBR, todayISO } from '../api'
import type { Appointment, ReportTemplate, User } from '../api'
import { IniciarExameButton } from './IniciarExame'

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  agendado: { label: 'Agendado', color: 'var(--text-3)', bg: 'var(--surface-2)' },
  em_sala: { label: 'Em sala', color: 'var(--accent)', bg: 'var(--accent-soft)' },
  aguardando_laudo: { label: 'Aguardando laudo', color: 'var(--primary)', bg: 'var(--primary-soft)' },
  liberado: { label: 'Laudo liberado', color: 'var(--ok)', bg: 'var(--ok-soft)' },
  faltou: { label: 'Faltou', color: 'var(--danger)', bg: 'var(--danger-soft)' },
}

// Eventos do Dia: agenda real do banco local. Daqui nascem os exames
// (captura) e os laudos.
export function Eventos({ user, onOpenCapture, onOpenReport }: {
  user: User
  onOpenCapture: (examId: string, patientId: string, patientName: string) => void
  onOpenReport: (reportId: string) => void
}) {
  const [date, setDate] = useState(todayISO())
  const [items, setItems] = useState<Appointment[]>([])
  const [templates, setTemplates] = useState<ReportTemplate[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')

  // novo agendamento
  const [showNew, setShowNew] = useState(false)
  const [patientQuery, setPatientQuery] = useState('')
  const [options, setOptions] = useState<{ id: string; name: string }[]>([])
  const [form, setForm] = useState({ patientId: '', examType: '', time: '08:00' })

  const load = useCallback(async (d: string) => {
    try {
      const out = await api.appointments.list(d)
      setItems(out.items)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao carregar agenda')
    }
  }, [])

  useEffect(() => { void load(date) }, [date, load])
  useEffect(() => {
    void api.templates.list().then((t) => {
      setTemplates(t.items)
      if (t.items.length) setForm((f) => ({ ...f, examType: f.examType || t.items[0].name }))
    }).catch(() => undefined)
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      void api.patients.list(patientQuery, 8)
        .then((r) => setOptions(r.items.map((p) => ({ id: p.id, name: p.name }))))
        .catch(() => undefined)
    }, 200)
    return () => clearTimeout(t)
  }, [patientQuery])

  const counts = {
    agendado: items.filter((i) => i.status === 'agendado').length,
    em_sala: items.filter((i) => i.status === 'em_sala').length,
    aguardando_laudo: items.filter((i) => i.status === 'aguardando_laudo').length,
    liberado: items.filter((i) => i.status === 'liberado').length,
  }

  async function startExam(a: Appointment) {
    setBusy(a.id)
    setError('')
    try {
      const out = await api.exams.create({
        patientId: a.patientId, type: a.examType, appointmentId: a.id,
      })
      onOpenCapture(out.exam.id, a.patientId, a.patientName ?? 'Paciente')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao iniciar exame')
    } finally {
      setBusy('')
    }
  }

  async function openOrCreateReport(a: Appointment) {
    setBusy(a.id)
    setError('')
    try {
      const exams = (await api.patients.exams(a.patientId)).items
      const exam = exams.find((e) => e.appointmentId === a.id) ?? exams[0]
      if (!exam) {
        setError('Nenhum exame encontrado para este agendamento.')
        return
      }
      const existing = (await api.reports.list({ examId: exam.id })).items[0]
      if (existing) {
        onOpenReport(existing.id)
      } else {
        const created = await api.reports.create(exam.id)
        onOpenReport(created.report.id)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao abrir laudo')
    } finally {
      setBusy('')
    }
  }

  async function createAppointment(e: React.FormEvent) {
    e.preventDefault()
    if (!form.patientId) { setError('Selecione o paciente.'); return }
    try {
      await api.appointments.create({
        patientId: form.patientId,
        examType: form.examType,
        startsAt: `${date}T${form.time}`,
        status: 'agendado',
      })
      setShowNew(false)
      setPatientQuery('')
      setForm({ patientId: '', examType: templates[0]?.name ?? '', time: '08:00' })
      await load(date)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao agendar')
    }
  }

  const canWrite = user.role === 'admin' || user.role === 'recepcao' || user.role === 'medico'

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 'none', padding: '14px 16px 12px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ height: 30, fontFamily: 'var(--font-mono)' }} />
          <div style={{ font: '600 16px/1.2 var(--font-sans)' }}>{formatDateBR(date)}</div>
          <div style={{ font: '400 12px/1 var(--font-mono)', color: 'var(--text-2)' }}>
            {items.length} exames agendados
          </div>
          <div style={{ flex: 1 }} />
          {canWrite && (
            <>
              <IniciarExameButton
                primary
                onStarted={(examId, patientId, patientName) => onOpenCapture(examId, patientId, patientName)}
              />
              <button className="btn" onClick={() => setShowNew((v) => !v)} style={{ height: 30 }}>
                {showNew ? 'Fechar' : 'Agendar'}
              </button>
            </>
          )}
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 9, flexWrap: 'wrap' }}>
          {(['agendado', 'em_sala', 'aguardando_laudo', 'liberado'] as const).map((k) => (
            <div key={k} className="card" style={{ flex: 1, minWidth: 130, padding: '9px 12px', background: 'var(--surface-2)' }}>
              <div style={{ font: '600 22px/1 var(--font-sans)', color: STATUS_STYLE[k].color }}>{counts[k]}</div>
              <div style={{ marginTop: 4, font: '500 10.5px/1 var(--font-sans)', letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-2)' }}>
                {STATUS_STYLE[k].label}
              </div>
            </div>
          ))}
        </div>

        {showNew && (
          <form onSubmit={createAppointment} className="fade-in" style={{ marginTop: 12, padding: 12, border: '1px solid var(--border)', borderRadius: 7, background: 'var(--surface-2)', display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="field" style={{ minWidth: 260 }}>
              <span>Paciente</span>
              <input className="input" placeholder="Buscar paciente…" value={patientQuery}
                onChange={(e) => { setPatientQuery(e.target.value); setForm((f) => ({ ...f, patientId: '' })) }} />
              {patientQuery && !form.patientId && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 5, background: 'var(--surface)', maxHeight: 140, overflowY: 'auto' }}>
                  {options.map((o) => (
                    <div key={o.id} onClick={() => { setForm((f) => ({ ...f, patientId: o.id })); setPatientQuery(o.name) }}
                      style={{ padding: '7px 9px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
                      {o.name}
                    </div>
                  ))}
                  {options.length === 0 && <div style={{ padding: '7px 9px', fontSize: 11, color: 'var(--text-3)' }}>Nenhum paciente — cadastre em Pacientes.</div>}
                </div>
              )}
            </div>
            <label className="field" style={{ minWidth: 200 }}>
              <span>Tipo de exame</span>
              <select className="input" value={form.examType} onChange={(e) => setForm((f) => ({ ...f, examType: e.target.value }))}>
                {templates.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                <option value="Retossigmoidoscopia">Retossigmoidoscopia</option>
              </select>
            </label>
            <label className="field" style={{ width: 110 }}>
              <span>Horário</span>
              <input className="input" type="time" value={form.time} onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))} style={{ fontFamily: 'var(--font-mono)' }} />
            </label>
            <button className="btn btn-primary" type="submit" style={{ height: 30, padding: '0 16px' }}>Agendar</button>
          </form>
        )}

        {error && <div style={{ marginTop: 10, padding: '8px 11px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: 'var(--surface)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '78px 1.5fr 1.3fr 1.1fr 1fr 150px 190px', gap: 0, padding: '7px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface-2)', position: 'sticky', top: 0, font: '600 9.5px/1 var(--font-sans)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
          <div>Horário</div><div>Paciente</div><div>Tipo de exame</div><div>Médico</div><div>Convênio</div><div>Status</div><div></div>
        </div>

        {items.map((a) => {
          const st = STATUS_STYLE[a.status] ?? STATUS_STYLE.agendado
          return (
            <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '78px 1.5fr 1.3fr 1.1fr 1fr 150px 190px', gap: 0, padding: '9px 16px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
              <div style={{ font: '600 12.5px/1 var(--font-mono)' }}>{(a.startsAt || '').slice(11, 16)}</div>
              <div style={{ font: '500 12.5px/1.3 var(--font-sans)' }}>{a.patientName}</div>
              <div style={{ font: '400 12px/1.3 var(--font-sans)', color: 'var(--text-2)' }}>{a.examType}</div>
              <div style={{ font: '400 12px/1.3 var(--font-sans)', color: 'var(--text-2)' }}>{a.professionalName || '—'}</div>
              <div style={{ font: '400 11.5px/1.3 var(--font-mono)', color: 'var(--text-2)' }}>{a.insuranceName || '—'}</div>
              <div><span className="badge" style={{ background: st.bg, color: st.color }}>{st.label.toUpperCase()}</span></div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                {(a.status === 'agendado' || a.status === 'em_sala') && (
                  <button className="btn" disabled={busy === a.id} onClick={() => void startExam(a)} style={{ height: 26, padding: '0 10px', fontSize: 11.5 }}>
                    {busy === a.id ? '…' : 'Iniciar exame'}
                  </button>
                )}
                {(a.status === 'aguardando_laudo' || a.status === 'em_sala') && (
                  <button className="btn btn-primary" disabled={busy === a.id} onClick={() => void openOrCreateReport(a)} style={{ height: 26, padding: '0 10px', fontSize: 11.5 }}>
                    Laudo
                  </button>
                )}
                {a.status === 'liberado' && (
                  <button className="btn" disabled={busy === a.id} onClick={() => void openOrCreateReport(a)} style={{ height: 26, padding: '0 10px', fontSize: 11.5 }}>
                    Ver laudo
                  </button>
                )}
              </div>
            </div>
          )
        })}

        {items.length === 0 && (
          <div style={{ padding: '36px 16px', textAlign: 'center', font: '400 12px/1.6 var(--font-mono)', color: 'var(--text-3)' }}>
            Nenhum exame agendado para {formatDateBR(date)}.
          </div>
        )}
      </div>
    </div>
  )
}
